import * as React from "react";

import { AgentRecord } from "../AgentRecord";

import { useAgents } from "./useAgents";

export interface AgentDirectory {
  /** Lookup by ``agent_id``. Empty until the underlying fetch settles. */
  byId: Map<string, AgentRecord>;
  /** Agents in the order the server returned them — useful for option lists. */
  agents: AgentRecord[];
  isLoading: boolean;
  isError: boolean;
}

/**
 * Every agent in the caller's org as lookups by id plus the raw list. Backs
 * both the Agent filter dropdown on the AgentThreads table (needs the list
 * as label/value options) and the per-row Agent cell renderer (needs
 * id → name lookup).
 *
 * Reads through {@link useAgents}, so it shares one fetch and one cache
 * entry with the Agents tab and the agent pickers, and the create, update
 * and delete mutations refresh it along with them.
 */
export function useAgentDirectory(orgId: string | undefined): AgentDirectory {
  const query = useAgents({ orgId });
  const agents = query.data ?? EMPTY_AGENTS;

  // Memoize on ``agents`` identity. TanStack Query produces a new array
  // reference only when the underlying data actually changes (it returns
  // the same reference for background refetches that resolve to the same
  // payload thanks to ``structuralSharing``), so cell-render consumers
  // that depend on ``byId`` won't churn on every revalidation.
  const byId = React.useMemo<Map<string, AgentRecord>>(() => {
    const map = new Map<string, AgentRecord>();
    for (const agent of agents) {
      map.set(agent.agent_id, agent);
    }
    return map;
  }, [agents]);

  return {
    byId,
    agents,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}

// Module-level constant so the ``agents`` fallback keeps a stable
// reference across renders while the query is pending — avoids
// recomputing ``byId`` on every render that happens before data arrives.
const EMPTY_AGENTS: AgentRecord[] = [];
